import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { environment } from '../../config/environment'
import { useAuthStore } from '../../stores/authStore'

interface Country {
  code: string;
  name: string;
}

interface GovernmentType {
  id: string;
  name: string;
  description: string;
  term_years: number;
}

const REGIONS = {
  africa: 'Africa',
  asia: 'Asia',
  europe: 'Europe',
  north_america: 'North America',
  south_america: 'South America',
  oceania: 'Oceania',
  middle_east: 'Middle East',
  caribbean: 'Caribbean'
};

const COUNTRIES_BY_REGION = {
  africa: [
    { code: 'NG', name: 'Nigeria' },
    { code: 'ZA', name: 'South Africa' },
    { code: 'EG', name: 'Egypt' },
    { code: 'KE', name: 'Kenya' },
    { code: 'MA', name: 'Morocco' }
  ],
  asia: [
    { code: 'CN', name: 'China' },
    { code: 'IN', name: 'India' },
    { code: 'JP', name: 'Japan' },
    { code: 'KR', name: 'South Korea' },
    { code: 'TH', name: 'Thailand' }
  ],
  europe: [
    { code: 'DE', name: 'Germany' },
    { code: 'FR', name: 'France' },
    { code: 'GB', name: 'United Kingdom' },
    { code: 'IT', name: 'Italy' },
    { code: 'ES', name: 'Spain' }
  ],
  north_america: [
    { code: 'US', name: 'United States' },
    { code: 'CA', name: 'Canada' },
    { code: 'MX', name: 'Mexico' }
  ],
  south_america: [
    { code: 'BR', name: 'Brazil' },
    { code: 'AR', name: 'Argentina' },
    { code: 'CL', name: 'Chile' },
    { code: 'CO', name: 'Colombia' }
  ],
  oceania: [
    { code: 'AU', name: 'Australia' },
    { code: 'NZ', name: 'New Zealand' },
    { code: 'FJ', name: 'Fiji' }
  ],
  middle_east: [
    { code: 'AE', name: 'United Arab Emirates' },
    { code: 'SA', name: 'Saudi Arabia' },
    { code: 'IL', name: 'Israel' },
    { code: 'TR', name: 'Turkey' }
  ],
  caribbean: [
    { code: 'JM', name: 'Jamaica' },
    { code: 'CU', name: 'Cuba' },
    { code: 'DO', name: 'Dominican Republic' }
  ]
};

function GamePage() {
  const [formData, setFormData] = useState({
    region: '',
    countryCode: '',
    countryName: '',
    difficulty: 'normal',
    gameMode: 'sandbox',
    governmentTypeId: '1'
  });
  
  const [availableCountries, setAvailableCountries] = useState<Country[]>([]);
  const [governmentTypes, setGovernmentTypes] = useState<GovernmentType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const navigate = useNavigate();
  const { token } = useAuthStore();

  // Load government types on component mount
  useEffect(() => {
    const loadGovernmentTypes = async () => {
      try {
        const response = await fetch(`${environment.getApiUrl('/api/admin/government-types')}`, {
          headers: token ? {
            'Authorization': `Bearer ${token}`
          } : {},
          credentials: 'include'
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.governmentTypes) {
            setGovernmentTypes(data.governmentTypes);
          }
        } else {
          // Fallback to default government types if API fails
          setGovernmentTypes([
            {
              id: '1',
              name: 'Presidential Republic',
              description: 'A democratic republic with a directly elected president',
              term_years: 4
            },
            {
              id: '2',
              name: 'Parliamentary Democracy',
              description: 'A democratic system where executive derives legitimacy from legislature',
              term_years: 5
            }
          ]);
        }
      } catch (error) {
        console.error('Failed to load government types:', error);
        // Use fallback data
        setGovernmentTypes([
          {
            id: '1',
            name: 'Presidential Republic',
            description: 'A democratic republic with a directly elected president',
            term_years: 4
          },
          {
            id: '2',
            name: 'Parliamentary Democracy', 
            description: 'A democratic system where executive derives legitimacy from legislature',
            term_years: 5
          }
        ]);
      }
    };

    loadGovernmentTypes();
  }, []);

  // Update available countries when region changes
  useEffect(() => {
    if (formData.region) {
      const countries = COUNTRIES_BY_REGION[formData.region as keyof typeof COUNTRIES_BY_REGION] || [];
      setAvailableCountries(countries);
      // Reset country selection when region changes
      setFormData(prev => ({ ...prev, countryCode: '', countryName: '' }));
    } else {
      setAvailableCountries([]);
    }
  }, [formData.region]);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    if (name === 'countryCode') {
      const selectedCountry = availableCountries.find(c => c.code === value);
      setFormData(prev => ({
        ...prev,
        countryCode: value,
        countryName: selectedCountry?.name || ''
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({});

    // Validate form
    const newErrors: Record<string, string> = {};
    if (!formData.region) newErrors.region = 'Please select a region';
    if (!formData.countryCode) newErrors.countryCode = 'Please select a country';
    if (!formData.difficulty) newErrors.difficulty = 'Please select a difficulty';
    if (!formData.governmentTypeId) newErrors.governmentTypeId = 'Please select a government type';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${environment.getApiUrl('/api/games')}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          countryName: formData.countryName,
          countryCode: formData.countryCode,
          region: formData.region,
          difficulty: formData.difficulty,
          gameMode: formData.gameMode,
          governmentTypeId: formData.governmentTypeId
        }),
        credentials: 'include'
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Game created successfully
        console.log('Game created:', data.game);
        navigate(`/game/${data.game.id}`);
      } else {
        setErrors({ general: data.error || 'Failed to create game' });
      }
    } catch (error) {
      console.error('Game creation error:', error);
      setErrors({ general: 'Network error. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Start New Game</h1>
          <p className="text-lg text-gray-600">
            Create your nation and begin your journey as a world leader
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {errors.general && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-red-800">{errors.general}</p>
            </div>
          )}

          {/* Country Selection */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="mr-2">🌍</span>
              Country Selection
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Region
                </label>
                <select
                  name="region"
                  value={formData.region}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select a region...</option>
                  {Object.entries(REGIONS).map(([key, name]) => (
                    <option key={key} value={key}>{name}</option>
                  ))}
                </select>
                {errors.region && (
                  <p className="mt-1 text-sm text-red-600">{errors.region}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Country
                </label>
                <select
                  name="countryCode"
                  value={formData.countryCode}
                  onChange={handleChange}
                  disabled={!formData.region}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="">Select a country...</option>
                  {availableCountries.map(country => (
                    <option key={country.code} value={country.code}>{country.name}</option>
                  ))}
                </select>
                {errors.countryCode && (
                  <p className="mt-1 text-sm text-red-600">{errors.countryCode}</p>
                )}
              </div>
            </div>
          </div>

          {/* Government & Difficulty Settings */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="mr-2">🏛️</span>
              Government Settings
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Government Type
                </label>
                <select
                  name="governmentTypeId"
                  value={formData.governmentTypeId}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {governmentTypes.map(govt => (
                    <option key={govt.id} value={govt.id}>
                      {govt.name} - {govt.description}
                    </option>
                  ))}
                </select>
                {errors.governmentTypeId && (
                  <p className="mt-1 text-sm text-red-600">{errors.governmentTypeId}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Difficulty
                </label>
                <select
                  name="difficulty"
                  value={formData.difficulty}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="easy">Easy - More resources, forgiving citizens</option>
                  <option value="normal">Normal - Balanced simulation</option>
                  <option value="hard">Hard - Limited resources, demanding citizens</option>
                  <option value="nightmare">Nightmare - Extreme challenges</option>
                </select>
                {errors.difficulty && (
                  <p className="mt-1 text-sm text-red-600">{errors.difficulty}</p>
                )}
              </div>
            </div>

            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Game Mode
              </label>
              <select
                name="gameMode"
                value={formData.gameMode}
                onChange={handleChange}
                className="w-full md:w-1/2 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="sandbox">Sandbox - Free play with no restrictions</option>
                <option value="campaign">Campaign - Structured scenarios</option>
                <option value="challenge">Challenge - Time-limited objectives</option>
              </select>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between">
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-8 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Creating Game...</span>
                </>
              ) : (
                <>
                  <span>🚀</span>
                  <span>Start Game</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default GamePage